import { useCallback, useEffect, useMemo, useState } from "react";
import { listManufacturingPlants, type MfgPlant } from "@/lib/manufacturingClient";
import { formatOutlookQty } from "@/lib/manufacturing/inventoryOutlookFormat";
import { WASTE_WALK_CAUTION, splitWasteLine } from "@/lib/manufacturing/wasteWalkMath";
import {
  addWasteWalkLine,
  closeWasteWalk,
  getWasteWalk,
  openWasteWalk,
  type UnmarkedWasteLine,
  type WasteWalkLine,
  type WasteWalkSession,
} from "@/lib/manufacturing/wasteWalkClient";
import { useManufacturingMaterials } from "./useManufacturingMaterials";
import { MaterialSearchableSelect } from "./MaterialSearchableSelect";
import { ManufacturingGateBanner } from "./ManufacturingGateBanner";
import { WalkDictationPad } from "./WalkDictationPad";
import { WasteWalkTrajectory } from "./WasteWalkTrajectory";
import { ghostBtnClass, inputClass, mfgPrimaryBtnClass } from "./manufacturingSurfaceClasses";
import { useManufacturingEntitlementGate } from "./useManufacturingEntitlementGate";

function usd(value: number): string {
  return `$${value.toFixed(2)}`;
}

export function WasteAssistantPanel() {
  const gate = useManufacturingEntitlementGate();
  const materials = useManufacturingMaterials({ enabled: gate.enabled });
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [plantId, setPlantId] = useState("");
  const [walk, setWalk] = useState<WasteWalkSession | null>(null);
  const [lines, setLines] = useState<WasteWalkLine[]>([]);
  const [unmarked, setUnmarked] = useState<UnmarkedWasteLine[]>([]);
  const [showCaution, setShowCaution] = useState(false);
  const [materialId, setMaterialId] = useState("");
  const [seen, setSeen] = useState("");
  const [useQty, setUseQty] = useState("");
  const [markedForUse, setMarkedForUse] = useState(false);
  const [removed, setRemoved] = useState(false);
  const [busy, setBusy] = useState(false);
  const [trajectoryRefresh, setTrajectoryRefresh] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const plantMaterials = useMemo(
    () => materials.materials.filter((row) => row.plant_id === plantId),
    [materials.materials, plantId],
  );
  const selected = plantMaterials.find((row) => row.id === materialId) ?? null;
  const seenQty = Number(seen);
  const salvageQty = useQty.trim() === "" ? 0 : Number(useQty);
  const split =
    seen.trim() !== "" && Number.isFinite(seenQty) && Number.isFinite(salvageQty)
      ? splitWasteLine(seenQty, salvageQty)
      : null;
  const prior = unmarked.find((row) => row.material_id === materialId) ?? null;

  const load = useCallback(async (id: string) => {
    const res = await getWasteWalk(id);
    if (!res.ok) {
      setError(res.error);
      setWalk(null);
      setLines([]);
      setUnmarked([]);
      return;
    }
    setWalk(res.data.walk);
    setLines(res.data.lines);
    setUnmarked(res.data.unmarked);
    setError(null);
  }, []);

  useEffect(() => {
    if (!gate.enabled) return;
    void listManufacturingPlants().then((res) => {
      if (!res.ok) return;
      const rows = Array.isArray(res.data.plants) ? res.data.plants : [];
      setPlants(rows);
      setPlantId((current) => current || rows[0]?.id || "");
    });
  }, [gate.enabled]);

  useEffect(() => {
    if (!gate.enabled || !plantId) return;
    void load(plantId);
  }, [gate.enabled, plantId, load]);

  async function acknowledge() {
    if (!plantId) return;
    setBusy(true);
    const res = await openWasteWalk(plantId);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setWalk(res.data.walk);
    setLines(res.data.lines);
    setUnmarked(res.data.unmarked);
    setShowCaution(false);
    setError(null);
  }

  async function confirm() {
    if (!walk || !selected || !split || !split.ok) return;
    setBusy(true);
    const res = await addWasteWalkLine({
      walk_id: walk.id,
      material_id: selected.id,
      qty_seen: split.line.qtySeen,
      qty_salvage: split.line.qtySalvage,
      marked_for_use: markedForUse,
      removed_from_area: removed,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setLines((current) => [...current, res.data.line]);
    setMaterialId("");
    setSeen("");
    setUseQty("");
    setMarkedForUse(false);
    setRemoved(false);
    setError(null);
    setTrajectoryRefresh((current) => current + 1);
    void load(plantId);
  }

  async function finish() {
    if (!walk) return;
    setBusy(true);
    const res = await closeWasteWalk(walk.id);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setWalk(null);
    setLines([]);
  }

  return (
    <div className="w-full space-y-4 text-left">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-zinc-100">Waste Assistant</h2>
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          Record what is sitting in waste areas. This is an early signal, not a consumption event.
        </p>
      </div>
      <ManufacturingGateBanner gate={gate} />
      {gate.enabled ? (
        <>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
            Plant
            <select className={inputClass} value={plantId} onChange={(e) => setPlantId(e.target.value)}>
              {plants.length === 0 ? <option value="">No plants</option> : null}
              {plants.map((plant) => (
                <option key={plant.id} value={plant.id}>
                  {plant.plant_code ? `${plant.plant_code} · ${plant.name}` : plant.name}
                </option>
              ))}
            </select>
          </label>
          {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
          {plantId ? <WasteWalkTrajectory plantId={plantId} refreshKey={trajectoryRefresh} /> : null}
          {!walk ? (
            <button type="button" className={mfgPrimaryBtnClass} disabled={!plantId || busy} onClick={() => setShowCaution(true)}>
              Start waste walk
            </button>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-gray-600 dark:text-zinc-400">Mark it and get it out of the waste area the first time you disposition it.</p>
                <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void finish()}>
                  Close walk
                </button>
              </div>
              <WalkDictationPad
                kind="waste"
                materials={plantMaterials}
                disabled={busy}
                onApply={({ material: next, primaryQty, secondaryQty }) => {
                  setMaterialId(next.id);
                  setSeen(String(primaryQty));
                  setUseQty(secondaryQty == null ? "" : String(secondaryQty));
                }}
              />
              <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-zinc-700">
                <MaterialSearchableSelect materials={plantMaterials} onSelect={(row) => setMaterialId(row.id)} />
                {selected ? (
                  <p className="text-sm text-gray-900 dark:text-zinc-100">
                    {selected.code} · {selected.name}
                  </p>
                ) : null}
                {prior ? (
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    Last line {new Date(prior.created_at).toLocaleString()}: seen {formatOutlookQty(prior.qty_seen)}, use{" "}
                    {formatOutlookQty(prior.qty_salvage)}
                    {prior.marked_for_use ? " (marked)" : ""}, scrap {formatOutlookQty(prior.qty_scrap)}
                    {prior.removed_from_area ? " (removed)" : ""}. Leave it off this walk if it is the same pile.
                  </p>
                ) : null}
                <div className="flex flex-wrap items-end gap-2">
                  <label className="text-sm text-gray-700 dark:text-zinc-300">
                    Seen
                    <input className={inputClass} inputMode="decimal" value={seen} onChange={(e) => setSeen(e.target.value)} />
                  </label>
                  <label className="text-sm text-gray-700 dark:text-zinc-300">
                    Tell them to use
                    <input className={inputClass} inputMode="decimal" value={useQty} onChange={(e) => setUseQty(e.target.value)} />
                  </label>
                </div>
                {split?.ok ? (
                  <p className="text-sm text-gray-700 dark:text-zinc-300">
                    Scrap {formatOutlookQty(split.line.qtyScrap)}. Seen {usd(split.line.qtySeen * (selected?.unit_cost ?? 0))}, salvage{" "}
                    {usd(split.line.qtySalvage * (selected?.unit_cost ?? 0))}, true waste{" "}
                    {usd(split.line.qtyScrap * (selected?.unit_cost ?? 0))}.
                  </p>
                ) : split && !split.ok ? (
                  <p className="text-sm text-red-700 dark:text-red-300">{split.error}</p>
                ) : null}
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
                  <input type="checkbox" checked={markedForUse} onChange={(e) => setMarkedForUse(e.target.checked)} />
                  Marked for the floor to use
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-zinc-300">
                  <input type="checkbox" checked={removed} onChange={(e) => setRemoved(e.target.checked)} />
                  Scrap marked and removed from the waste area
                </label>
                <button
                  type="button"
                  className={mfgPrimaryBtnClass}
                  disabled={!split?.ok || !selected || busy}
                  onClick={() => void confirm()}
                >
                  Confirm disposition
                </button>
              </div>
              <ul className="space-y-2">
                {lines.map((line) => (
                  <li key={line.id} className="rounded-lg border border-gray-200 p-3 text-sm dark:border-zinc-700">
                    <p className="font-medium text-gray-900 dark:text-zinc-100">
                      {line.material_code} · {line.material_name}
                    </p>
                    <p className="text-gray-600 dark:text-zinc-400">
                      Seen {formatOutlookQty(line.qty_seen)}, use {formatOutlookQty(line.qty_salvage)}
                      {line.marked_for_use ? " marked" : ""}, scrap {formatOutlookQty(line.qty_scrap)}
                      {line.removed_from_area ? " removed" : ""}. Posted scrap {formatOutlookQty(line.scrap_posted_qty)}, returned{" "}
                      {formatOutlookQty(line.salvage_posted_qty)}. {usd(line.qty_scrap * line.unit_cost)} true waste.
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      ) : null}
      {showCaution ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="max-w-lg space-y-3 rounded-xl bg-white p-4 text-gray-900 shadow-lg dark:bg-zinc-900 dark:text-zinc-100">
            <h3 className="text-base font-semibold">Before you count this pile</h3>
            <p className="text-sm">{WASTE_WALK_CAUTION}</p>
            <div className="flex justify-end gap-2">
              <button type="button" className={ghostBtnClass} onClick={() => setShowCaution(false)}>
                Cancel
              </button>
              <button type="button" className={mfgPrimaryBtnClass} disabled={busy} onClick={() => void acknowledge()}>
                Acknowledge
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
